import * as THREE from "three";

/**
 * Procedural textures for LIQUID effects — the hose weapons' streams, their
 * spray/mist, their impact splash, and the fine ripple normal the animated water
 * surface samples. Canvas-generated (no asset files), module-cached, one GPU
 * upload each, generated lazily on first use and never regenerated.
 *
 * Why these exist (mission `graphics`, 2026-07-31): the sperm/water guns drew a
 * flat-shaded cylinder plus tumbling icosahedron "gobs". At any distance that
 * reads as a straight plastic rod with rocks in it, because a constant-colour
 * cylinder has NO lengthwise structure — nothing tells the eye the liquid is
 * moving, and nothing breaks the silhouette's hard edge. A ligament texture
 * scrolled along the stream axis supplies both: internal flow, and a chewed
 * alpha edge that stops the rod reading as a solid tube.
 *
 * Budget: 128x256 stream, 128² splash, 128² ripple normal — 3 small textures
 * total, ~130 KB of VRAM, generated once for the tab's lifetime.
 */

/** Deterministic hash noise. Math.random() would be fine (this is client-only
 *  presentation, not the shared deterministic sim — invariant 7 does not reach
 *  here), but a fixed pattern makes an A/B screenshot reproducible. */
function rnd(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

function cvs(w: number, h: number): { c: HTMLCanvasElement; g: CanvasRenderingContext2D } {
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  return { c, g: c.getContext("2d")! };
}

let streamCanvas: HTMLCanvasElement | null = null;

/**
 * The stream body: white ROPEY LIGAMENTS on transparent, tiling vertically so it
 * can be scrolled along the jet axis forever. V runs along the flow (the core
 * cylinders are `rotateX(PI/2)`, so the lathe/height axis — and therefore V — is
 * the stream direction).
 *
 * Drawn as a set of sine-displaced vertical strands of varying width and alpha,
 * each closing back on its own phase at the wrap so the tile is seamless, plus a
 * few travelling "surges" (bright fatter blobs) that read as pressure pulses.
 * The horizontal edges (u near 0 and 1) are faded so the cylinder's silhouette
 * dissolves instead of ending on a hard rim.
 */
function streamCanvasOf(): HTMLCanvasElement {
  if (streamCanvas) return streamCanvas;
  const W = 128, H = 256;
  const { c, g } = cvs(W, H);
  const r = rnd(0x51ea3);
  g.clearRect(0, 0, W, H);
  // A solid base column FIRST. Strands alone (the first version of this) left the
  // tube almost transparent — measured in the browser, the stream barely read at
  // gameplay distance. The liquid is dense; the strands are the structure INSIDE
  // it, not the whole of it.
  // 2026-08-24 (mission `graphics`): the base was 0.5 and the tube read as a
  // sparse cluster of drops rather than a continuous liquid rope (baseline
  // capture). Denser base + a brighter, wider spine below give the core a solid
  // wet body; the strands and froth ride ON that instead of BEING it.
  g.fillStyle = "rgba(255,255,255,0.62)";
  g.fillRect(0, 0, W, H);
  g.globalCompositeOperation = "lighter";
  // 30 strands. Each is a column walked in y with a wavy x whose phase is an
  // INTEGER number of cycles over H, so the strand meets itself at the wrap.
  for (let s = 0; s < 30; s++) {
    const baseX = r() * W;
    const cycles = 1 + ((r() * 3) | 0);
    const amp = 2 + r() * 9;
    const wide = 1.5 + r() * 5;
    const a = 0.16 + r() * 0.5;
    const ph = r() * Math.PI * 2;
    g.strokeStyle = `rgba(255,255,255,${a.toFixed(3)})`;
    g.lineWidth = wide;
    g.lineCap = "round";
    g.beginPath();
    for (let y = 0; y <= H; y += 4) {
      const x = baseX + Math.sin((y / H) * cycles * Math.PI * 2 + ph) * amp;
      if (y === 0) g.moveTo(x, y); else g.lineTo(x, y);
    }
    g.stroke();
  }
  // travelling surges — fat soft blobs so the stream has lumps, not just lines
  for (let i = 0; i < 14; i++) {
    const x = r() * W, y = r() * H, rad = 6 + r() * 16;
    const grd = g.createRadialGradient(x, y, 0, x, y, rad);
    grd.addColorStop(0, `rgba(255,255,255,${(0.35 + r() * 0.4).toFixed(3)})`);
    grd.addColorStop(1, "rgba(255,255,255,0)");
    g.fillStyle = grd;
    g.fillRect(x - rad, y - rad, rad * 2, rad * 2);
    // wrap copy so a blob straddling the seam appears on both sides
    if (y < rad) { g.fillStyle = grd; g.save(); g.translate(0, H); g.fillRect(x - rad, y - rad, rad * 2, rad * 2); g.restore(); }
    if (y > H - rad) { g.save(); g.translate(0, -H); g.fillRect(x - rad, y - rad, rad * 2, rad * 2); g.restore(); }
  }
  // fine FROTH speckle — tiny bright droplets peppered over the body so the
  // liquid reads as aerated spray, not a smooth gel. Cheap, and it survives the
  // mip chain as a subtle sparkle at range instead of a flat wash.
  for (let i = 0; i < 90; i++) {
    const x = r() * W, y = r() * H, rad = 0.6 + r() * 1.6;
    g.fillStyle = `rgba(255,255,255,${(0.2 + r() * 0.45).toFixed(3)})`;
    g.beginPath(); g.arc(x, y, rad, 0, Math.PI * 2); g.fill();
  }
  // a bright continuous spine down the middle of the tube (u ~ 0.5 is the part
  // of the cylinder facing the camera at the default view) — reads as the dense
  // unbroken core inside the spray. 2026-08-24: widened and brightened, plus a
  // tight hot inner filament, so the stream has an unmistakable coherent core
  // that atomises toward its edges rather than reading as a loose cone of gobs.
  g.globalCompositeOperation = "lighter";
  const spine = g.createLinearGradient(0, 0, W, 0);
  spine.addColorStop(0, "rgba(255,255,255,0)");
  spine.addColorStop(0.5, "rgba(255,255,255,0.5)");
  spine.addColorStop(1, "rgba(255,255,255,0)");
  g.fillStyle = spine;
  g.fillRect(0, 0, W, H);
  const hot = g.createLinearGradient(0, 0, W, 0);
  hot.addColorStop(0.40, "rgba(255,255,255,0)");
  hot.addColorStop(0.5, "rgba(255,255,255,0.55)");
  hot.addColorStop(0.60, "rgba(255,255,255,0)");
  g.fillStyle = hot;
  g.fillRect(0, 0, W, H);
  // fade the U edges to alpha 0 so the tube silhouette dissolves
  g.globalCompositeOperation = "destination-in";
  const edge = g.createLinearGradient(0, 0, W, 0);
  edge.addColorStop(0, "rgba(0,0,0,0)");
  edge.addColorStop(0.16, "rgba(0,0,0,1)");
  edge.addColorStop(0.84, "rgba(0,0,0,1)");
  edge.addColorStop(1, "rgba(0,0,0,0)");
  g.fillStyle = edge;
  g.fillRect(0, 0, W, H);
  g.globalCompositeOperation = "source-over";
  streamCanvas = c;
  return c;
}

/** A fresh CanvasTexture over the shared stream canvas. Callers get their OWN
 *  texture object because each stream scrolls its own `offset.y` — sharing one
 *  texture would lock the sperm and water hoses to the same phase, and (worse)
 *  a single `offset` cannot express two flow speeds. The canvas — the expensive
 *  part — is shared; only the small GPU upload is duplicated. */
export function streamTexture(): THREE.CanvasTexture {
  const t = new THREE.CanvasTexture(streamCanvasOf());
  t.wrapS = THREE.ClampToEdgeWrapping; // U is around the tube: must not tile
  t.wrapT = THREE.RepeatWrapping;      // V is along the flow: scrolls forever
  t.repeat.set(1, 1);
  return t;
}

let splashTex: THREE.CanvasTexture | null = null;

/** The wet impact: a burst of scattered water DROPLETS with a soft wet core,
 *  billboarded at the point the stream meets geometry. Without this the stream
 *  simply ended in mid-air (or worse, passed through the wall) — an impact is
 *  most of what makes a hose read as pressurised liquid rather than a laser.
 *
 *  2026-09-03 (mission `graphics`): the previous version was 34 long thin radial
 *  streaks over an alpha-1.0 white core. Under the bloom pass that reads as a
 *  firework STARBURST, not water (baseline capture, fxwater-village-b) — the
 *  exact "cheap" tell the mission calls out. A real splash is a cloud of ROUND
 *  droplets thrown off a surface, densest in a mid-radius crown, with only a few
 *  short ligament flecks and NO blown-out white centre. So: a soft sub-1.0 core,
 *  a dense crown of rounded droplets weighted to a mid radius, a handful of short
 *  ligament streaks (a third the old length), and two faint spreading rings that
 *  read as the sheet fanning out across the surface. Bluish/warm tint is left to
 *  the per-weapon material colour. */
export function splashTexture(): THREE.CanvasTexture {
  if (splashTex) return splashTex;
  const S = 128;
  const { c, g } = cvs(S, S);
  const cx = S / 2, cy = S / 2;
  const r = rnd(0x9a71c);
  g.globalCompositeOperation = "lighter";
  // SOFT CORE first — a wet dome, capped well under 1.0 so bloom lifts it to a
  // glow instead of a white-hot star. This is the single biggest reason the old
  // impact read as a spark.
  const core = g.createRadialGradient(cx, cy, 0, cx, cy, S * 0.30);
  core.addColorStop(0, "rgba(255,255,255,0.72)");
  core.addColorStop(0.45, "rgba(255,255,255,0.30)");
  core.addColorStop(1, "rgba(255,255,255,0)");
  g.fillStyle = core;
  g.fillRect(0, 0, S, S);
  // SPREADING SHEET — two faint thin rings so the splash reads as liquid fanning
  // outward over the surface rather than a point flash.
  for (let ri = 0; ri < 2; ri++) {
    const rr = S * (0.34 + ri * 0.12);
    g.strokeStyle = `rgba(255,255,255,${(0.13 - ri * 0.04).toFixed(3)})`;
    g.lineWidth = 3 - ri;
    g.beginPath(); g.arc(cx, cy, rr, 0, Math.PI * 2); g.stroke();
  }
  // THE CROWN — a dense cloud of ROUND droplets, weighted toward a mid radius
  // (a real splash sprays a ring, not a filled disc), each with a tiny lit cap so
  // it reads as a wet bead, not a flat dot.
  for (let i = 0; i < 60; i++) {
    const ang = r() * Math.PI * 2;
    // bias distance to a mid crown: sqrt pulls toward the edge, the 0.10 floor
    // keeps a few near the core so the burst isn't a hollow ring.
    const dist = S * (0.10 + 0.42 * Math.sqrt(r()));
    const rad = 1.1 + r() * 3.6 * (1 - dist / (S * 0.55)); // bigger beads nearer the core
    const x = cx + Math.cos(ang) * dist, y = cy + Math.sin(ang) * dist;
    g.fillStyle = `rgba(255,255,255,${(0.18 + r() * 0.34).toFixed(3)})`;
    g.beginPath(); g.arc(x, y, Math.max(0.8, rad), 0, Math.PI * 2); g.fill();
    // a smaller hot cap on the upper-left of the bead → it reads as a 3D droplet
    g.fillStyle = `rgba(255,255,255,${(0.14 + r() * 0.2).toFixed(3)})`;
    g.beginPath(); g.arc(x - rad * 0.3, y - rad * 0.3, Math.max(0.5, rad * 0.42), 0, Math.PI * 2); g.fill();
  }
  // a FEW short ligament flecks — the last shreds of the stream tearing apart on
  // contact. Short (a third of the old length) and thin, so they suggest motion
  // without rebuilding the starburst.
  for (let i = 0; i < 12; i++) {
    const ang = r() * Math.PI * 2;
    const r0 = S * (0.12 + r() * 0.1), r1 = r0 + S * (0.07 + r() * 0.12);
    g.strokeStyle = `rgba(255,255,255,${(0.14 + r() * 0.24).toFixed(3)})`;
    g.lineWidth = 1 + r() * 2;
    g.lineCap = "round";
    g.beginPath();
    g.moveTo(cx + Math.cos(ang) * r0, cy + Math.sin(ang) * r0);
    g.lineTo(cx + Math.cos(ang) * r1, cy + Math.sin(ang) * r1);
    g.stroke();
  }
  g.globalCompositeOperation = "source-over";
  splashTex = new THREE.CanvasTexture(c);
  // Shared, tab-lifetime singleton (like dropletGeometry / JET_FX_GEO): guard it
  // so a stray fx mesh left in the scene at teardown can't dispose the one splash
  // texture every future hose impact reuses (renderer disposeSubtree honours this).
  splashTex.userData.shared = true;
  return splashTex;
}

let rippleTex: THREE.CanvasTexture | null = null;

/**
 * Tiling tangent-space NORMAL map of fine capillary ripples for the animated
 * water surface. The water shader already had a big analytic swell with an
 * analytic normal, but nothing between "20 m wave" and "flat mirror" — so close
 * up the sea was a perfectly smooth plastic sheet whose only detail was the
 * fresnel. Two scrolling samples of this at different scales give the surface
 * high-frequency slope, which is what the sun glint and the fresnel need in
 * order to sparkle instead of smear.
 *
 * Built as a height field of summed, wrapping sine trains (which tiles exactly by
 * construction — integer cycles across the texture), then Sobel'd.
 */
export function rippleNormalTexture(): THREE.CanvasTexture {
  if (rippleTex) return rippleTex;
  const S = 128;
  const { c, g } = cvs(S, S);
  const r = rnd(0x2b17f);
  const h = new Float32Array(S * S);
  // 9 wrapping wave trains, random integer frequency vectors
  for (let w = 0; w < 9; w++) {
    const fx = ((r() * 7) | 0) - 3, fz = ((r() * 7) | 0) - 3;
    if (fx === 0 && fz === 0) continue;
    const amp = 1 / (1 + Math.hypot(fx, fz));
    const ph = r() * Math.PI * 2;
    for (let y = 0; y < S; y++) {
      for (let x = 0; x < S; x++) {
        h[y * S + x] += Math.sin(((x / S) * fx + (y / S) * fz) * Math.PI * 2 + ph) * amp;
      }
    }
  }
  const img = g.createImageData(S, S);
  const d = img.data;
  const at = (x: number, y: number): number => h[((y + S) % S) * S + ((x + S) % S)];
  const strength = 1.6;
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const dx = at(x + 1, y) - at(x - 1, y);
      const dy = at(x, y + 1) - at(x, y - 1);
      let nx = -dx * strength, ny = -dy * strength, nz = 1;
      const len = Math.hypot(nx, ny, nz);
      nx /= len; ny /= len; nz /= len;
      const i = (y * S + x) * 4;
      d[i] = (nx * 0.5 + 0.5) * 255;
      d[i + 1] = (ny * 0.5 + 0.5) * 255;
      d[i + 2] = (nz * 0.5 + 0.5) * 255;
      d[i + 3] = 255;
    }
  }
  g.putImageData(img, 0, 0);
  rippleTex = new THREE.CanvasTexture(c);
  rippleTex.wrapS = rippleTex.wrapT = THREE.RepeatWrapping;
  return rippleTex;
}

let dropGeo: THREE.BufferGeometry | null = null;

/**
 * A TEARDROP: fat leading head, long tapering tail, axis = +Z (matching the jet
 * code's `_jetAxis`). Replaces the icosahedron "gob".
 *
 * This is the non-texture half of the stream change. A tumbling icosahedron is a
 * ROCK — it has flat facets, a hard silhouette and no direction, so 16 of them
 * flying in a cone read as gravel, not liquid. A lathe-of-revolution teardrop
 * aligned to the flow and stretched by distance reads as a ligament of liquid
 * being torn off the stream, which is what actually happens in a real jet.
 *
 * One shared geometry for every drop of every player's jet (module cached, never
 * disposed) — 8 profile samples x 9 radial = ~112 tris, comparable to the
 * icosahedron it replaces once you count that it is drawn instanced either way.
 */
export function dropletGeometry(): THREE.BufferGeometry {
  if (dropGeo) return dropGeo;
  const pts: THREE.Vector2[] = [];
  const N = 8;
  for (let i = 0; i <= N; i++) {
    const t = i / N;
    // radius peaks around t=0.62 (toward the head at t=1) and runs to a point at
    // t=0; the 1e-4 floor keeps the lathe from degenerating at the tip.
    const rad = Math.max(1e-4, Math.sin(Math.PI * Math.pow(t, 1.45)) * 0.5);
    pts.push(new THREE.Vector2(rad, t - 0.5));
  }
  const g = new THREE.LatheGeometry(pts, 9);
  g.rotateX(Math.PI / 2); // lathe axis +Y -> +Z, so +Z is the direction of travel
  g.userData.shared = true;
  dropGeo = g;
  return g;
}
